import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import type { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import type {
  PaidMediaOverviewGranularity,
  PaidMediaOverviewLeadingPlatform,
  PaidMediaOverviewTrendMetrics,
  PaidMediaOverviewTrendPoint,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewText,
} from "../../utils/paidMediaOverviewFormat";
import { formatPaidMediaDateText } from "../../utils/paidMediaNumberFormat";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import {
  OVERVIEW_GOOGLE_COLOR,
  OVERVIEW_GOOGLE_REVENUE_COLOR,
  OVERVIEW_GRID_COLOR,
  OVERVIEW_META_COLOR,
  OVERVIEW_META_REVENUE_COLOR,
  formatOverviewBucketLabel,
  overviewAxisProps,
} from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

type TrendMetric = "spendRevenue" | "revenue" | "spend" | "purchases" | "roas" | "cpa";

interface TrendChartRow {
  label: string;
  metaSpend: number | null;
  googleSpend: number | null;
  metaRevenue: number | null;
  googleRevenue: number | null;
  metaPurchases: number | null;
  googlePurchases: number | null;
  metaRoas: number | null;
  googleRoas: number | null;
  metaCpa: number | null;
  googleCpa: number | null;
  point: PaidMediaOverviewTrendPoint;
}

const METRIC_OPTIONS: Array<{ value: TrendMetric; labelKey: string; tooltipId?: string }> = [
  { value: "spendRevenue", labelKey: "paidMediaOverview.trend.metricSpendRevenue" },
  { value: "revenue", labelKey: "paidMediaOverview.metrics.revenue", tooltipId: "revenue" },
  { value: "spend", labelKey: "paidMediaOverview.metrics.spend", tooltipId: "spend" },
  {
    value: "purchases",
    labelKey: "paidMediaOverview.metrics.purchases",
    tooltipId: "paidPurchases",
  },
  { value: "roas", labelKey: "paidMediaOverview.metrics.roas", tooltipId: "roas" },
  { value: "cpa", labelKey: "paidMediaOverview.metrics.cpa", tooltipId: "costPerPurchase" },
];

const SUBTITLE_KEY: Record<PaidMediaOverviewGranularity, string> = {
  daily: "paidMediaOverview.trend.subtitleDaily",
  weekly: "paidMediaOverview.trend.subtitleWeekly",
  monthly: "paidMediaOverview.trend.subtitleMonthly",
};

const GRANULARITY_LABEL_KEY: Record<PaidMediaOverviewGranularity, string> = {
  daily: "paidMediaOverview.granularity.daily",
  weekly: "paidMediaOverview.granularity.weekly",
  monthly: "paidMediaOverview.granularity.monthly",
};

const TREND_CHART_MARGIN = { top: 16, right: 10, bottom: 5, left: 0 };
const TREND_Y_AXIS_WIDTH = 88;

type TrendLegendItem = {
  key: string;
  label: string;
  itemClass: string;
  labelClass?: string;
  swatchClass?: string;
  lineColor?: string;
};

function seriesKey(platform: "meta" | "google", metric: TrendMetric): string {
  return `${platform}${metric.charAt(0).toUpperCase()}${metric.slice(1)}`;
}

const toChartRow = (
  point: PaidMediaOverviewTrendPoint,
  granularity: PaidMediaOverviewGranularity,
  t: TFunction,
  locale: string
): TrendChartRow => ({
  label: formatOverviewBucketLabel(
    point?.date,
    granularity,
    date => t("paidMediaOverview.trend.weekOf", { date }),
    locale
  ),
  metaSpend: point?.meta_ads?.spend ?? null,
  googleSpend: point?.google_ads?.spend ?? null,
  metaRevenue: point?.meta_ads?.revenue ?? null,
  googleRevenue: point?.google_ads?.revenue ?? null,
  metaPurchases: point?.meta_ads?.purchases ?? null,
  googlePurchases: point?.google_ads?.purchases ?? null,
  metaRoas: point?.meta_ads?.roas ?? null,
  googleRoas: point?.google_ads?.roas ?? null,
  metaCpa: point?.meta_ads?.cost_per_purchase ?? null,
  googleCpa: point?.google_ads?.cost_per_purchase ?? null,
  point,
});

const TooltipColumn: FC<{ heading: string; headingClass: string; metrics: PaidMediaOverviewTrendMetrics | null }> = ({
  heading,
  headingClass,
  metrics,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <div className={`paid-media-overview__chart-tooltip-heading ${headingClass}`}>{heading}</div>
      <div>
        {t("paidMediaOverview.metrics.spend")} {formatOverviewText(metrics?.formatted_spend)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.revenue")} {formatOverviewText(metrics?.formatted_revenue)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.purchases")}{" "}
        {metrics?.formatted_purchases ?? formatOverviewNumber(metrics?.purchases)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.roas")}{" "}
        {metrics?.formatted_roas ?? formatOverviewMultiplier(metrics?.roas)}
      </div>
      <div>
        {t("paidMediaOverview.metrics.cpa")}{" "}
        {formatOverviewText(metrics?.formatted_cost_per_purchase)}
      </div>
    </div>
  );
};

const TrendTooltip: FC<TooltipProps<ValueType, NameType>> = ({ active, payload }) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload as TrendChartRow | undefined;
  if (!row?.point) {
    return null;
  }

  const leaderLabel = overviewPlatformLabel(t, row.point.leader);

  return (
    <div className="paid-media-overview__chart-tooltip">
      <div className="paid-media-overview__chart-tooltip-title">
        {formatPaidMediaDateText(row.point.date) || row.label}
      </div>
      <div className="paid-media-overview__chart-tooltip-columns">
        <TooltipColumn
          heading={t("paidMediaOverview.platforms.meta")}
          headingClass="paid-media-overview__legend-label--meta-revenue"
          metrics={row.point.meta_ads}
        />
        <TooltipColumn
          heading={t("paidMediaOverview.platforms.google")}
          headingClass="paid-media-overview__legend-label--google-revenue"
          metrics={row.point.google_ads}
        />
      </div>
      {leaderLabel ? (
        <div className="paid-media-overview__chart-tooltip-footer">
          {t("paidMediaOverview.trend.tooltipLeader")} <strong>{leaderLabel}</strong>
          {row.point.leader_reason ? ` — ${row.point.leader_reason}` : null}
        </div>
      ) : null}
    </div>
  );
};

interface SpendRevenueTrendCardProps {
  series: PaidMediaOverviewTrendPoint[];
  granularity: PaidMediaOverviewGranularity;
  leadingPlatform: PaidMediaOverviewLeadingPlatform | null;
  currency: string | null;
  loading: boolean;
}

const SpendRevenueTrendCard: FC<SpendRevenueTrendCardProps> = ({
  series,
  granularity,
  leadingPlatform,
  currency,
  loading,
}) => {
  const { t, i18n } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();
  const [metric, setMetric] = useState<TrendMetric>("spendRevenue");

  const data: TrendChartRow[] = useMemo(
    () => (series ?? []).map(point => toChartRow(point, granularity, t, i18n.language)),
    [series, granularity, t, i18n.language]
  );

  const tickFormatter = (value: number) => {
    if (metric === "roas") {
      return `${value}x`;
    }
    return metric === "purchases"
      ? formatOverviewNumber(value)
      : formatOverviewCurrency(value, currency);
  };

  const activeMetricLabel = t(
    METRIC_OPTIONS.find(option => option.value === metric)?.labelKey ?? ""
  );
  const leadingPlatformName = overviewPlatformLabel(t, leadingPlatform?.platform);

  const legendItems = useMemo((): TrendLegendItem[] => {
    if (metric === "spendRevenue") {
      return [
        {
          key: "metaSpend",
          label: t("paidMediaOverview.trend.seriesMetaSpend"),
          itemClass: "paid-media-overview__chart-legend-item--meta",
          swatchClass: "paid-media-overview__chart-legend-swatch--meta",
        },
        {
          key: "googleSpend",
          label: t("paidMediaOverview.trend.seriesGoogleSpend"),
          itemClass: "paid-media-overview__chart-legend-item--google",
          swatchClass: "paid-media-overview__chart-legend-swatch--google",
        },
        {
          key: "metaRevenue",
          label: t("paidMediaOverview.trend.seriesMetaRevenue"),
          itemClass: "paid-media-overview__chart-legend-item--meta-revenue",
          labelClass: "paid-media-overview__legend-label--meta-revenue",
          lineColor: OVERVIEW_META_REVENUE_COLOR,
        },
        {
          key: "googleRevenue",
          label: t("paidMediaOverview.trend.seriesGoogleRevenue"),
          itemClass: "paid-media-overview__chart-legend-item--google-revenue",
          labelClass: "paid-media-overview__legend-label--google-revenue",
          lineColor: OVERVIEW_GOOGLE_REVENUE_COLOR,
        },
      ];
    }

    const seriesLabelKeys: Record<Exclude<TrendMetric, "spendRevenue">, [string, string]> = {
      spend: [
        "paidMediaOverview.trend.seriesMetaSpend",
        "paidMediaOverview.trend.seriesGoogleSpend",
      ],
      revenue: [
        "paidMediaOverview.trend.seriesMetaRevenue",
        "paidMediaOverview.trend.seriesGoogleRevenue",
      ],
      purchases: [
        "paidMediaOverview.conversion.seriesMetaPurchases",
        "paidMediaOverview.conversion.seriesGooglePurchases",
      ],
      roas: [
        "paidMediaOverview.efficiency.seriesMetaRoas",
        "paidMediaOverview.efficiency.seriesGoogleRoas",
      ],
      cpa: [
        "paidMediaOverview.efficiency.seriesMetaCpa",
        "paidMediaOverview.efficiency.seriesGoogleCpa",
      ],
    };

    const [metaLabelKey, googleLabelKey] = seriesLabelKeys[metric];

    return [
      {
        key: "meta",
        label: t(metaLabelKey),
        itemClass: "paid-media-overview__chart-legend-item--meta",
        labelClass: "paid-media-overview__legend-label--meta-revenue",
        lineColor: OVERVIEW_META_COLOR,
      },
      {
        key: "google",
        label: t(googleLabelKey),
        itemClass: "paid-media-overview__chart-legend-item--google",
        labelClass: "paid-media-overview__legend-label--google-revenue",
        lineColor: OVERVIEW_GOOGLE_COLOR,
      },
    ];
  }, [metric, t]);

  const renderChartBody = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!data.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        {metric === "spendRevenue" ? (
          <ComposedChart data={data} margin={chartRtl.margin(TREND_CHART_MARGIN)}>
            <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
            <XAxis dataKey="label" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
            <YAxis
              yAxisId="l"
              width={TREND_Y_AXIS_WIDTH}
              orientation={chartRtl.yAxisOrientation}
              tickFormatter={value => formatOverviewCurrency(value, currency)}
              {...overviewAxisProps}
            />
            <YAxis
              yAxisId="r"
              orientation={chartRtl.oppositeYAxisOrientation}
              width={TREND_Y_AXIS_WIDTH}
              tickFormatter={value => formatOverviewCurrency(value, currency)}
              {...overviewAxisProps}
            />
            <Tooltip
              wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
              content={<TrendTooltip />}
            />
            <Bar
              yAxisId="l"
              dataKey="metaSpend"
              name={t("paidMediaOverview.trend.seriesMetaSpend")}
              fill={OVERVIEW_META_COLOR}
              fillOpacity={0.75}
              radius={[3, 3, 0, 0]}
            />
            <Bar
              yAxisId="l"
              dataKey="googleSpend"
              name={t("paidMediaOverview.trend.seriesGoogleSpend")}
              fill={OVERVIEW_GOOGLE_COLOR}
              fillOpacity={0.85}
              radius={[3, 3, 0, 0]}
            />
            <Line
              yAxisId="r"
              type="monotone"
              dataKey="metaRevenue"
              name={t("paidMediaOverview.trend.seriesMetaRevenue")}
              stroke={OVERVIEW_META_REVENUE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="r"
              type="monotone"
              dataKey="googleRevenue"
              name={t("paidMediaOverview.trend.seriesGoogleRevenue")}
              stroke={OVERVIEW_GOOGLE_REVENUE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </ComposedChart>
        ) : (
          <LineChart data={data} margin={chartRtl.margin(TREND_CHART_MARGIN)}>
            <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
            <XAxis dataKey="label" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
            <YAxis
              width={TREND_Y_AXIS_WIDTH}
              orientation={chartRtl.yAxisOrientation}
              {...overviewAxisProps}
              tickFormatter={tickFormatter}
            />
            <Tooltip
              wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
              content={<TrendTooltip />}
            />
            <Line
              type="monotone"
              dataKey={seriesKey("meta", metric)}
              name={`${t("paidMediaOverview.platforms.meta")} ${activeMetricLabel}`}
              stroke={OVERVIEW_META_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey={seriesKey("google", metric)}
              name={`${t("paidMediaOverview.platforms.google")} ${activeMetricLabel}`}
              stroke={OVERVIEW_GOOGLE_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    );
  };

  return (
    <article className="paid-media-panel paid-media-overview__trend-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>{t("paidMediaOverview.trend.title")}</h3>
          <p className="paid-media-overview__panel-subtitle">{t(SUBTITLE_KEY[granularity])}</p>
        </div>
      </div>

      <div
        className="paid-media-overview__trend-metric-tabs"
        role="group"
        aria-label={t("paidMediaOverview.trend.metricTabsAria")}
      >
        {METRIC_OPTIONS.map(option => {
          const label = t(option.labelKey);
          const selected = metric === option.value;
          return (
            <div key={option.value} className="paid-media-overview__trend-metric-tab">
              <button
                type="button"
                onClick={() => setMetric(option.value)}
                aria-pressed={selected}
                className={
                  selected
                    ? "paid-media-overview__trend-metric-btn paid-media-overview__trend-metric-btn--active"
                    : "paid-media-overview__trend-metric-btn"
                }
              >
                {label}
              </button>
              {option.tooltipId ? (
                <OverviewMetricTip id={option.tooltipId} label={label} />
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="paid-media-overview__chart paid-media-overview__chart--trend">
        {renderChartBody()}
      </div>

      <div
        className="paid-media-overview__chart-legend-row"
        aria-label={t("paidMediaOverview.trend.legendAria")}
      >
        {legendItems.map(item => (
          <div
            key={item.key}
            className={`paid-media-overview__chart-legend-item ${item.itemClass}`}
          >
            {item.swatchClass ? (
              <span
                className={`paid-media-overview__chart-legend-swatch ${item.swatchClass}`}
                aria-hidden
              />
            ) : (
              <LegendLineIcon color={item.lineColor ?? OVERVIEW_META_COLOR} />
            )}
            <span className={item.labelClass}>{item.label}</span>
          </div>
        ))}
      </div>

      {leadingPlatformName ? (
        <div className="paid-media-overview__callout">
          <strong>
            {t("paidMediaOverview.trend.calloutLabel", {
              period: t(GRANULARITY_LABEL_KEY[granularity]),
            })}
          </strong>{" "}
          {t("paidMediaOverview.trend.calloutLeader", {
            platform: leadingPlatformName,
            reason: formatOverviewText(leadingPlatform?.reason),
          })}
        </div>
      ) : null}
    </article>
  );
};

const LegendLineIcon: FC<{ color: string }> = ({ color }) => (
  <svg width="32" height="14" viewBox="0 0 32 14" fill="none" aria-hidden="true">
    <path
      strokeWidth="4"
      fill="none"
      stroke={color}
      d="M0,7 H10.67 A5.33,5.33,0,1,1,21.33,7 H32 M21.33,7 A5.33,5.33,0,1,1,10.67,7"
      className="recharts-legend-icon"
    />
  </svg>
);

export default SpendRevenueTrendCard;
